import { Box, Button, Container, Stack, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import DownloadScoreCardReport from './DownloadReportPages/DownloadScoreCardReport';
import GreyToGreyDiv from '../../common/grey_grey_div.png';
import { useTranslation } from 'react-i18next';
import DownloadCashboardReport from './DownloadReportPages/DownloadCashboardReport';
import DownloadLiquidProficiencyReport from './DownloadReportPages/DownloadLiquidProficiencyReport';
import DownloadNearTermLiquidityReport from './DownloadReportPages/DownloadNearTermLiquidityReport';
import DownloadAccountOptimizationReport from './DownloadReportPages/DownloadAccountOptimizationReport';
import DownloadTreasuryServicesReport from './DownloadReportPages/DownloadTreasuryServicesReport';
import { PrintChartContext, PrintChartProvider } from './PrintChartContext';

interface DownloadReportProps {
  reportDate: string;
  setPrintState: (val: boolean) => void;
  previewMode?: boolean;
}

const DownloadReport = (props: DownloadReportProps) => {
  return (
    <PrintChartProvider>
      <DownloadReportContent {...props} />
    </PrintChartProvider>
  );
};

const DownloadReportContent = (props: DownloadReportProps) => {
  const { t } = useTranslation();
  const { pendingCharts } = React.useContext(PrintChartContext);
  const printStartedRef = React.useRef(false);

  useEffect(() => {
    if (props.previewMode) return;
    if (pendingCharts > 0) return;
    if (printStartedRef.current) return;

    const timeout = setTimeout(() => {
      if (printStartedRef.current) return;
      printStartedRef.current = true;
      window.print();
      props.setPrintState(false);
    }, 500);

    return () => clearTimeout(timeout);
  }, [pendingCharts, props.previewMode, props.setPrintState]);

  return (
    <Box
      sx={{
        printColorAdjust: 'exact',
        WebkitPrintColorAdjust: 'exact',
        WebkitFilter: 'opacity(1)',
        '& .MuiTableContainer-root': {
          width: '100%',
          maxWidth: '100%',
          overflowX: 'visible'
        },
        '& .MuiTable-root': {
          width: '100%',
          tableLayout: 'fixed'
        },
        '& .MuiTableCell-root': {
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          paddingTop: 0.75,
          paddingBottom: 0.75,
          paddingLeft: 0.75,
          paddingRight: 0.75,
          fontSize: '12px',
          lineHeight: 1.25
        },
        '& .MuiTableCell-root .MuiTypography-root': {
          fontSize: 'inherit',
          lineHeight: 'inherit'
        },
        '& .MuiTypography-noWrap': {
          overflow: 'visible',
          textOverflow: 'clip',
          whiteSpace: 'normal'
        },
        '& .paper-section-content': {
          paddingLeft: 0,
          paddingRight: 0
        },
        '& .tpo-tooltip-trigger': {
          display: 'none'
        },
        '@media print': {
          '& *': {
            boxShadow: 'none !important'
          }
        },
        ...(props.previewMode && {
          '& *': {
            boxShadow: 'none !important'
          }
        })
      }}
    >
      {props.previewMode && (
        <Stack
          className="no-print"
          direction="row"
          spacing={2}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: 2,
            backgroundColor: '#f2f2f2',
            borderBottom: '1px solid #d9d9d9'
          }}
        >
          <Typography sx={{ marginRight: 'auto', color: 'text.primary', fontWeight: 650 }}>
            Print preview mode
          </Typography>
          <Button
            variant="outlined"
            onClick={() => props.setPrintState(false)}
            sx={{ textTransform: 'none' }}
          >
            Close preview
          </Button>
          <Button variant="contained" onClick={() => window.print()} sx={{ textTransform: 'none' }}>
            Print now
          </Button>
        </Stack>
      )}
      <Box sx={{ backgroundColor: '#f2f2f2' }}>
        <img src={GreyToGreyDiv} style={{ width: '100%', display: 'block', objectFit: 'cover' }} alt="header-divider" />
      </Box>
      <Box
        sx={{
          padding: '25px',
          backgroundColor: 'white'
        }}
      >
        <Container>
          <Typography
            sx={{
              fontSize: '48px',
              color: 'primary.main'
            }}
          >
            {t('analysesSummary.title')}
          </Typography>

          <Typography
            sx={{
              fontSize: '18px',
              color: 'text.primary'
            }}
          >
            {t('analysesSummary.detail')}
          </Typography>
          <DownloadScoreCardReport />
          <DownloadCashboardReport reportDate={props.reportDate} />
          <DownloadLiquidProficiencyReport />
          <DownloadNearTermLiquidityReport />
          <DownloadAccountOptimizationReport />
          <DownloadTreasuryServicesReport />
        </Container>
      </Box>
    </Box>
  );
};

export default DownloadReport;
